import {
  ObjectiveType,
  type CreateEducationChapterDto,
  type CreateObjectiveDto,
  type CreateObjectiveVideoQuestionDto,
  type EducationDetailChapterApi,
} from "@/features/education-management/types/education-management.types";

export interface LiveStreamQuestionOption {
  id: string;
  text: string;
  isCorrect: boolean;
}

export interface LiveStreamQuestion {
  id: string;
  text: string;
  options: LiveStreamQuestionOption[];
  allowMultiple: boolean;
  imageFileID?: string;
  imagePreview?: string;
  screenDurationValue: string;
  screenDurationUnit: "dakika" | "saat";
}

export const EDUCATION_FORMATS_WITH_LIVE_QUESTIONS = [
  "online",
  "non-unit-based",
] as const;

export function educationFormatSupportsLiveQuestions(format: string): boolean {
  return (EDUCATION_FORMATS_WITH_LIVE_QUESTIONS as readonly string[]).includes(
    format,
  );
}

export function resolveLiveJitsiUrl(educationContent: string): string {
  const firstLine =
    educationContent.trim().split(/\r?\n/).find((s) => s.trim()) ?? "";
  const t = firstLine.trim();
  if (/^https?:\/\//i.test(t)) return t.slice(0, 2000);
  return "https://meet.jit.si/keyp-canli-egitim";
}

export function screenDurationToTimestamp(
  value: string,
  unit: "dakika" | "saat",
): string {
  const n = parseInt(value, 10);
  const totalMin =
    Number.isNaN(n) || n < 0 ? 2 : unit === "saat" ? n * 60 : n;
  const capped = Math.min(Math.max(totalMin, 0), 23 * 60 + 59);
  const h = Math.floor(capped / 60);
  const m = capped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`;
}

export function timestampToScreenDuration(ts: string): {
  value: string;
  unit: "dakika" | "saat";
} {
  const parts = (ts || "00:02:00").split(":");
  const h = parseInt(parts[0] ?? "0", 10) || 0;
  const m = parseInt(parts[1] ?? "0", 10) || 0;
  const totalMin = h * 60 + m;
  if (totalMin <= 0) return { value: "2", unit: "dakika" };
  return { value: String(totalMin), unit: "dakika" };
}

export function isLiveStreamQuestionsChapter(
  ch: EducationDetailChapterApi,
): boolean {
  // Canlı soru bölümü: JITSI objective + videoQuestions (unit içi videolarda da videoQuestions olabilir)
  return (ch.objectives ?? []).some((o) => {
    const type = o.type;
    const vq = o.videoQuestions;
    return (
      type === ObjectiveType.JITSI &&
      Array.isArray(vq) &&
      vq.length > 0
    );
  });
}

export function extractLiveStreamQuestionsFromChapters(
  chapters: EducationDetailChapterApi[] | undefined,
): LiveStreamQuestion[] {
  if (!chapters?.length) return [];
  const out: LiveStreamQuestion[] = [];
  for (const ch of chapters) {
    for (const obj of ch.objectives ?? []) {
      if (obj.type !== ObjectiveType.JITSI) continue;
      const vqs = obj.videoQuestions;
      if (!vqs?.length) continue;
      for (const vq of vqs) {
        const dur = timestampToScreenDuration(vq.timestamp ?? "00:02:00");
        const opts = (vq.options ?? []).map((o) => ({
          id: o.id ?? crypto.randomUUID(),
          text: o.optionText ?? "",
          isCorrect: !!o.isCorrect,
        }));
        out.push({
          id: crypto.randomUUID(),
          text: vq.questionText ?? "",
          allowMultiple: !!vq.allowMultipleSelection,
          imageFileID: vq.imageFileID,
          imagePreview: undefined,
          screenDurationValue: dur.value,
          screenDurationUnit: dur.unit,
          options:
            opts.length >= 2
              ? opts
              : [
                  ...opts,
                  ...Array.from({ length: 2 - opts.length }, () => ({
                    id: crypto.randomUUID(),
                    text: "",
                    isCorrect: false,
                  })),
                ],
        });
      }
    }
  }
  return out;
}

function toVideoQuestionDto(
  q: LiveStreamQuestion,
  order: number,
): CreateObjectiveVideoQuestionDto | null {
  const optionsWithText = q.options.filter((o) => o.text.trim() !== "");
  if (optionsWithText.length < 2 || !q.text.trim()) return null;
  const correctOnes = optionsWithText.filter((o) => o.isCorrect);
  if (correctOnes.length < 1) return null;
  if (!q.allowMultiple && correctOnes.length !== 1) return null;

  return {
    questionText: q.text.trim(),
    timestamp: screenDurationToTimestamp(
      q.screenDurationValue,
      q.screenDurationUnit,
    ),
    order,
    imageFileID: q.imageFileID,
    allowMultipleSelection: q.allowMultiple,
    options: optionsWithText.map((o, i) => ({
      optionText: o.text.trim(),
      order: i,
      isCorrect: o.isCorrect,
    })),
  };
}

export function buildLiveStreamQuestionsChapterDto(
  questions: LiveStreamQuestion[],
  externalUrl: string,
  chapterTitle: string,
  objectiveTitle: string,
): CreateEducationChapterDto | null {
  const videoQuestions = questions
    .map((q, i) => toVideoQuestionDto(q, i))
    .filter((x): x is CreateObjectiveVideoQuestionDto => x !== null);
  if (!videoQuestions.length) return null;

  const objective: CreateObjectiveDto = {
    type: ObjectiveType.JITSI,
    title: objectiveTitle.slice(0, 200),
    externalUrl,
    order: 0,
    isVisibleToUser: true,
    videoQuestions,
  };

  return {
    title: chapterTitle.slice(0, 200),
    objectives: [objective],
  };
}
